import { useSelector } from "react-redux";
import ItemDisplayPage from "../../components/ItemDisplay/ItemDisplayPage";
import Modal from "../../components/Modal/Modal";
import { RootState } from "../../store";
import { AppState } from "../../appSlice";
import { applyProductFilters, getDefaultProductFilterState } from "../../components/ItemDisplay/Product/functions";
import ProductFilterModalBody from "../../components/ItemDisplay/Product/ProductFilterModalBody";
import { ProductOverviewLink } from "../../components/ItemOverviewLink/ItemOverviewLink";
import { FIREBASE_STORAGE_URL } from "../../app-settings";
import { Button } from "../../components/Control/Button/Button";
import { DbProductOverview, ProductDetailsCard } from "../../models/Product";
import { classNames } from "../../style/common";
import { ArrowRightEndOnRectangleIcon } from "@heroicons/react/24/outline";
import { getProduct } from "../../repository/productRepository";
import { showFailure } from "../../components/NotificationStack/notificationSlice";
import { useState } from "react";

export type StarterDeckImportProps = {
    isOpen: boolean,
    onOpenChange: (isOpen: boolean) => void,
    onImport: (variantQuantity: Record<string, number>) => void
}

export function StarterDeckImport(props: StarterDeckImportProps) {
    const { products } = useSelector<RootState, AppState>(x => x.app);
    const [isLoading, setIsLoading] = useState(false);

    const defaultProductState = () => {
        const newDefaultProductState = getDefaultProductFilterState();

        const starterDecks = newDefaultProductState.productTypeState.find(x => x.data === "Starter Deck");
        if (starterDecks) {
            starterDecks.isChecked = true;
        }

        return newDefaultProductState;
    }

    const importStarterDeck = (productOverview: DbProductOverview) => {
        setIsLoading(true);

        getProduct(productOverview.id)
            .then((result) => {
                if (!result.cards) {
                    showFailure("There are no cards in this product.");
                    return;
                }

                const variantsWithQuantity = result.cards.filter(x => x.quantity && x.quantity > 0);
                const variantQuantityDictionary: Record<string, number> = {};

                for (const card of variantsWithQuantity) {
                    variantQuantityDictionary[card.variantId] = card.quantity!;
                }

                props.onImport(variantQuantityDictionary);
            })
            .catch((e) => {
                showFailure("Error fetching product.");
            })
            .finally(() => {
                setIsLoading(false);
                props.onOpenChange(false);
            });
    }

    return (
        <Modal isOpen={props.isOpen} onOpenChange={(isOpen) => props.onOpenChange(isOpen)} title="Import Starter Deck" classNames={{ dialogPanel: "!w-[400px]" }}>
            <ItemDisplayPage
                displayStateId={"starter-deck-import"}
                enableScrollRefresh={false}
                items={products}
                toolbarPosition="relative"
                hideFilterButton={true}
                hideResetButton={true}
                classNames={{ itemsContainer: "!grid-cols-1 space-y-16 mt-4", toolbar: { container: "mt-4" } }}
                filter={(items, filters) => applyProductFilters(items, filters)}
                getDefaultFilterState={defaultProductState}
                modalBody={ProductFilterModalBody}
                itemComponentGenerator={(item) => {
                    return (
                        <div key={item.id}>
                            <div className={
                                classNames(
                                    "w-full overflow-hidden rounded-md bg-tertiary/60 border border-color-border border-opacity-0",
                                    "h-56 lg:h-72 xl:h-80"
                                )
                            }>
                                <img
                                    src={`${FIREBASE_STORAGE_URL}/${item.imageUrl}`}
                                    alt={`${item.name} image`}
                                    loading="lazy"
                                    className="h-full w-full object-contain p-5 object-center"
                                />
                            </div>

                            <Button
                                color="subtle"
                                className="flex gap-1 hover:cursor-pointer w-full my-2 justify-center"
                                onClick={() => importStarterDeck(item)}
                            >
                                <ArrowRightEndOnRectangleIcon className="w-5 h-5" />
                                Import
                            </Button>
                            <h3 className="text-sm font-semibold">
                                {item.name}
                            </h3>

                            <div className="flex justify-between">
                                <p
                                    className="mt-1 text-sm">
                                    {item.released}
                                </p>
                            </div>

                        </div>
                    )
                }} />

            <Button
                disabled={false}
                onClick={() => props.onOpenChange(false)}
                color="component"
                className="w-full mt-12"
            >
                Cancel
            </Button>
        </Modal>
    )
}